/**
 * Step 1 — Plantilla del ciclo + datos básicos.
 *
 * Captura: name, client, code (opcional), content_language, lifecycle template.
 *
 * El idioma de los documentos oficiales es inmutable post-create (Doc 19 §2.2).
 *
 * AI-1.f.bug — los lifecycles ya NO son hardcoded. Hace
 * `GET /templates?template_type=lifecycle&template_status=published` y
 * fetchea el `current_version` de cada uno para popular nombre/descripción
 * localizados. Al seleccionar un lifecycle, dispara `set_lifecycle` que
 * además de guardarlo, computa las phaseDefs para Step 3.
 *
 * OT-0055 — solo se ofrecen los ciclos de metodologías publicadas (el
 * backend lo exige al crear el proyecto) y se preselecciona el de la
 * metodología predeterminada de la organización o, si no tiene, el de la
 * del producto: la misma precedencia que aplica el backend cuando el
 * proyecto no elige ciclo.
 */

import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueries, useQuery } from '@tanstack/react-query'
import { CheckCircle2, Loader2 } from 'lucide-react'

import type { Methodology } from '../../../api/methodologies'
import type { ContentLanguage } from '../../../api/projects'
import {
  getCurrentVersion,
  listTemplates,
  type Template,
  type TemplateVersionWithDefinition,
} from '../../../api/templates'
import { useAccessToken } from '../../../auth/useAccessToken'
import { useMethodologies } from '../../../hooks/useMethodologies'
import {
  buildPhaseDefsFromDefinition,
  type LifecycleTemplateOption,
  type PhaseDef,
  type WizardState,
  type WizardAction,
} from './types'
import { DEFAULT_LOCALE } from '../../../i18n/config'

interface Step1Props {
  state: WizardState
  onPatch: (
    patch: Partial<
      Pick<WizardState, 'name' | 'client' | 'code' | 'contentLanguage'>
    >,
  ) => void
  /** AI-1.f.bug: para disparar `set_lifecycle` que sincroniza phaseDefs. */
  dispatch: React.Dispatch<WizardAction>
}

export function Step1Template({ state, onPatch, dispatch }: Step1Props) {
  const { t, i18n } = useTranslation('projects')
  const { accessToken } = useAccessToken()
  const methodologiesQuery = useMethodologies()
  const { byId: methodologiesById } = methodologiesQuery

  // 1. Lista de lifecycle templates publicados visibles al tenant.
  const listQuery = useQuery({
    queryKey: ['templates', 'lifecycle', 'published'],
    queryFn: () =>
      listTemplates(accessToken!, {
        template_type: 'lifecycle',
        template_status: 'published',
      }),
    enabled: !!accessToken,
    staleTime: 60_000,
  })

  // 2. Para cada lifecycle, fetch del current_version (necesario para
  // sacar el i18n localizado y la lista de phases que Step 3 va a usar).
  // Un useQueries paralelo evita N round-trips secuenciales.
  const versionQueries = useQueries({
    queries: (listQuery.data ?? [])
      .filter((tpl) => tpl.current_version_id)
      .map((tpl) => ({
        queryKey: ['template-current', tpl.id],
        queryFn: () => getCurrentVersion(accessToken!, tpl.id),
        enabled: !!accessToken,
        staleTime: 60_000,
      })),
  })

  // 3. Junta `Template` + `TemplateVersion.definition` en
  // LifecycleTemplateOption listo para renderizar y dispatch. Solo ciclos
  // de metodologías publicadas.
  const options: LifecycleTemplateOption[] = useMemo(() => {
    const templates = listQuery.data ?? []
    const preferredId = preferredMethodologyId(methodologiesById)
    const out: LifecycleTemplateOption[] = []
    for (let i = 0; i < templates.length; i++) {
      const tpl = templates[i]!
      if (!tpl.current_version_id) continue
      const methodology = tpl.methodology_id
        ? methodologiesById.get(tpl.methodology_id)
        : undefined
      if (methodology?.status !== 'published') continue
      const versionRes = versionQueries[i]
      if (!versionRes?.data) continue
      out.push(
        buildOption(tpl, versionRes.data, methodology, methodology.id === preferredId),
      )
    }
    // La preseleccionada primero, luego las del sistema y las del tenant;
    // alfabético por nombre.
    return out.sort(
      (a, b) =>
        Number(b.isPreferred) - Number(a.isPreferred) ||
        Number(b.isSystemDefault) - Number(a.isSystemDefault) ||
        a.name.localeCompare(b.name, i18n.language || DEFAULT_LOCALE),
    )
  }, [listQuery.data, versionQueries, methodologiesById, i18n.language])

  // Selección automática: el ciclo de la metodología predeterminada (el
  // primero de la lista). Si ya hay uno seleccionado que aún existe en la
  // lista, no toca nada.
  useEffect(() => {
    if (options.length === 0) return
    if (state.lifecycleTemplateId) {
      // Verifica que la selección actual siga válida; si el user borró
      // el template fuera de banda, fallback al primero.
      const stillValid = options.some(
        (o) => o.id === state.lifecycleTemplateId,
      )
      if (stillValid) return
    }
    const first = options[0]!
    selectLifecycle(first)
    // Solo cuando cambia la lista de options o la selección queda inválida.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options])

  // Map id → versión completa (con definition) para lookup O(1).
  const versionByTemplateId = useMemo(() => {
    const m = new Map<string, TemplateVersionWithDefinition>()
    const tpls = listQuery.data ?? []
    for (let i = 0; i < tpls.length; i++) {
      const tpl = tpls[i]!
      const data = versionQueries[i]?.data as
        | TemplateVersionWithDefinition
        | undefined
      if (data) m.set(tpl.id, data)
    }
    return m
  }, [listQuery.data, versionQueries])

  function selectLifecycle(opt: LifecycleTemplateOption) {
    const versionData = versionByTemplateId.get(opt.id)
    const definition = versionData?.definition ?? null
    const phaseDefs: PhaseDef[] = buildPhaseDefsFromDefinition(
      definition,
      state.contentLanguage,
    )
    dispatch({
      type: 'set_lifecycle',
      template: opt,
      phaseDefs,
    })
  }

  const isLoading =
    listQuery.isLoading ||
    methodologiesQuery.isLoading ||
    versionQueries.some((q) => q.isLoading)
  const isError =
    listQuery.isError ||
    methodologiesQuery.isError ||
    versionQueries.some((q) => q.isError)

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('wizard.step1.fields.name')}
          </span>
          <input
            type="text"
            value={state.name}
            onChange={(e) => onPatch({ name: e.target.value })}
            placeholder={t('wizard.step1.fields.name_placeholder')}
            className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('wizard.step1.fields.client')}
          </span>
          <input
            type="text"
            value={state.client}
            onChange={(e) => onPatch({ client: e.target.value })}
            placeholder={t('wizard.step1.fields.client_placeholder')}
            className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('wizard.step1.fields.code')}
          </span>
          <input
            type="text"
            value={state.code}
            onChange={(e) => onPatch({ code: e.target.value })}
            placeholder={t('wizard.step1.fields.code_placeholder')}
            className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('wizard.step1.fields.language')}{' '}
            <span className="text-ink-subtle">
              {t('wizard.step1.fields.language_immutable')}
            </span>
          </span>
          <select
            value={state.contentLanguage}
            onChange={(e) =>
              onPatch({ contentLanguage: e.target.value as ContentLanguage })
            }
            className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm"
          >
            <option value="es">Español (es)</option>
            <option value="en">English (en)</option>
          </select>
          <span className="mt-1 block text-[11px] text-ink-subtle">
            {t('wizard.step1.fields.language_help')}
          </span>
        </label>
      </div>

      <p className="text-sm font-medium mb-3">
        {t('wizard.step1.templates_title')}
      </p>

      {isLoading && options.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-ink-subtle py-6">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('wizard.step1.templates_loading')}
        </div>
      ) : isError ? (
        <div className="rounded-md border border-critical/40 bg-critical-soft/30 p-3 text-sm text-ink">
          {t('wizard.step1.templates_error')}
        </div>
      ) : options.length === 0 ? (
        <div className="rounded-md border border-review/40 bg-review-soft/40 p-3 text-sm text-ink-muted">
          {t('wizard.step1.templates_empty')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {options.map((opt) => (
            <LifecycleCard
              key={opt.id}
              option={opt}
              selected={state.lifecycleTemplateId === opt.id}
              onSelect={() => selectLifecycle(opt)}
            />
          ))}
        </div>
      )}
      <p className="mt-3 text-[11px] text-ink-subtle italic">
        {t('wizard.step1.templates_dynamic_hint')}
      </p>
    </div>
  )
}

function LifecycleCard({
  option,
  selected,
  onSelect,
}: {
  option: LifecycleTemplateOption
  selected: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation('projects')
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={
        selected
          ? 'border border-brand bg-brand-soft/40 rounded-md p-4 text-left transition-colors'
          : 'border border-line rounded-md p-4 text-left hover:border-line-strong transition-colors'
      }
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-ink truncate">
          {option.name}
        </span>
        {selected ? (
          <CheckCircle2 className="w-4 h-4 text-brand shrink-0" />
        ) : null}
      </div>
      {option.description ? (
        <p className="text-xs text-ink-muted mt-1 font-body line-clamp-2">
          {option.description}
        </p>
      ) : null}
      <p className="text-xs text-ink mt-2 flex items-center gap-2 min-w-0">
        <span className="truncate">
          {t('wizard.step1.templates_methodology', {
            methodology: option.methodologyName,
          })}
        </span>
        {option.isPreferred ? (
          <span className="shrink-0 px-1 py-px rounded bg-brand-soft text-brand text-[9px] uppercase tracking-wider">
            {t('wizard.step1.templates_preferred_badge')}
          </span>
        ) : null}
      </p>
      <p className="text-[10px] font-mono text-ink-subtle mt-1 truncate">
        {option.templateKey} · v{option.version}
        {option.isSystemDefault ? (
          <span className="ml-2 inline-block px-1 py-px rounded bg-line text-ink-subtle text-[9px] uppercase tracking-wider">
            {t('wizard.step1.templates_system_badge')}
          </span>
        ) : (
          <span className="ml-2 inline-block px-1 py-px rounded bg-brand/15 text-brand text-[9px] uppercase tracking-wider">
            {t('wizard.step1.templates_tenant_badge')}
          </span>
        )}
      </p>
    </button>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function buildOption(
  tpl: Template,
  version: TemplateVersionWithDefinition,
  methodology: Methodology,
  isPreferred: boolean,
): LifecycleTemplateOption {
  return {
    id: tpl.id,
    templateKey: tpl.template_key,
    name: tpl.name,
    description: tpl.description,
    version: version.version,
    isSystemDefault: tpl.is_system_default,
    methodologyName: methodology.name,
    isPreferred,
  }
}

/** La metodología cuyo ciclo se preselecciona: la predeterminada de la
 * organización o, si no tiene, la del producto (misma precedencia que
 * `_resolve_lifecycle_template_id` en el backend). */
function preferredMethodologyId(
  byId: ReadonlyMap<string, Methodology>,
): string | undefined {
  const all = [...byId.values()]
  return (all.find((m) => m.is_tenant_default) ?? all.find((m) => m.is_default))?.id
}

